import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Ingress } from "../../../api/controlPlaneApi";
import {
  MinioIngressRule,
  MinioIngressStatus,
  MinioIngressTLS,
} from "../../../api/ingressRpcApi";
import { LoadingStatus } from "common/types";
import { LabelKeyPair } from "screens/common/types";
import {
  addIngressAsync,
  fetchIngressForEdit,
  updateIngressAsync,
} from "./AddIngressAsync";

export interface IAddIngress {
  addIngressLoading: LoadingStatus;
  addError: string;
  editMode: boolean;
  addComplete: string;
  getIngressLoading: boolean;
  selectedSite: string;
  namespace: string;
  name: string;
  labels: LabelKeyPair[];
  annotations: LabelKeyPair[];
  ingressRules: MinioIngressRule[];
  ingressTLS: MinioIngressTLS[];
  ingressStatus: MinioIngressStatus[];
  IngressProcessed: Ingress | null;
  validationErrors: { [key: string]: string };
}

const initialState: IAddIngress = {
  addIngressLoading: "idle",
  addError: "",
  editMode: false,
  addComplete: "",
  getIngressLoading: false,
  selectedSite: "",
  namespace: "",
  name: "",
  labels: [
    {
      key: "",
      value: "",
    },
  ],
  annotations: [
    {
      key: "",
      value: "",
    },
  ],
  ingressRules: [],
  ingressTLS: [],
  ingressStatus: [],
  IngressProcessed: null,
  validationErrors: {},
};

export const addIngressSlice = createSlice({
  name: "addIngress",
  initialState,
  reducers: {
    setIngressSelectedSite: (state, action: PayloadAction<string>) => {
      state.selectedSite = action.payload;
    },
    setIngressName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setIngressNamespace: (state, action: PayloadAction<string>) => {
      state.namespace = action.payload;
    },
    setIngressLabels: (state, action: PayloadAction<LabelKeyPair[]>) => {
      state.labels = action.payload;
    },
    setIngressAnnotations: (state, action: PayloadAction<LabelKeyPair[]>) => {
      state.annotations = action.payload;
    },
    setIngressRules: (state, action: PayloadAction<MinioIngressRule[]>) => {
      state.ingressRules = action.payload;
    },
    setIngressTLS: (state, action: PayloadAction<MinioIngressTLS[]>) => {
      state.ingressTLS = action.payload;
    },
    resetAddIngressForm: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(addIngressAsync.pending, (state) => {
        state.addIngressLoading = "loading";
      })
      .addCase(addIngressAsync.rejected, (state) => {
        state.addIngressLoading = "failed";
      })
      .addCase(addIngressAsync.fulfilled, (state, action) => {
        state.addIngressLoading = "idle";
        state.IngressProcessed = action.payload;
        state.addComplete = "yes";
      })
      .addCase(updateIngressAsync.pending, (state) => {
        state.addIngressLoading = "loading";
      })
      .addCase(updateIngressAsync.rejected, (state) => {
        state.addIngressLoading = "failed";
      })
      .addCase(updateIngressAsync.fulfilled, (state, action) => {
        state.addIngressLoading = "idle";
        state.IngressProcessed = action.payload;
        state.addComplete = "yes";
      })
      .addCase(fetchIngressForEdit.pending, (state) => {
        state.editMode = true;
        state.addIngressLoading = "loading";
      })
      .addCase(fetchIngressForEdit.rejected, (state) => {
        state.addIngressLoading = "failed";
      })
      .addCase(fetchIngressForEdit.fulfilled, (state, action) => {
        state.addIngressLoading = "idle";
        state.name = action.payload.metadata?.name!;
        state.labels = Object.entries(action.payload.metadata?.labels!).map(
          ([key, value]) => ({ key, value }),
        );
        state.annotations = Object.entries(
          action.payload.metadata?.annotations!,
        ).map(([key, value]) => ({ key, value }));
        state.ingressRules = action.payload.spec!.rules!;
        state.ingressTLS = action.payload.spec!.tls!;
      });
  },
});

export const {
  resetAddIngressForm,
  setIngressSelectedSite,
  setIngressName,
  setIngressNamespace,
  setIngressLabels,
  setIngressAnnotations,
  setIngressRules,
  setIngressTLS,
} = addIngressSlice.actions;

export default addIngressSlice.reducer;
